<template>
  <div class="form-item-table">
    <div
      v-if="hasGroupLabel"
      class="form-item-table-title aisea-text-16-700-dark-gray"
    >
      {{ formatLabel(group.label) }}
    </div>
    <div class="form-item-table-wrapper">
      <table class="form-item-table-content">
        <thead v-if="hasColumnHeaders">
          <tr>
            <th v-if="hasRowHeaders" class="form-item-table-corner"></th>
            <th
              v-for="(colHeader, colIndex) in columnHeaders"
              :key="`col-${colIndex}`"
              class="form-item-table-col-header aisea-text-14-700-dark-gray"
              :class="{ required: colHeader.required && isEditing }"
            >
              {{ formatLabel(colHeader.label) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in tableMatrix" :key="`row-${rowIndex}`">
            <td
              v-if="hasRowHeaders"
              class="form-item-table-row-header aisea-text-14-700-dark-gray"
              :class="{ required: isRowRequired(rowIndex) && isEditing }"
            >
              <span
                v-for="(line, lineIndex) in getRowHeaderLines(rowIndex)"
                :key="lineIndex"
                class="row-header-line"
                >{{ line }}</span
              >
            </td>
            <td
              v-for="(item, colIndex) in row"
              :key="`cell-${rowIndex}-${colIndex}`"
              class="form-item-table-cell"
            >
              <template v-if="item">
                <!-- 閲覧モード（編集中でも項目単位 editable=false は read-only 表示） -->
                <div
                  v-if="!isEditing || !isItemEditable(item)"
                  class="cell-display aisea-text-14-400-dark-gray"
                >
                  {{ getCellDisplayValue(item) }}
                </div>
                <!-- 編集モード -->
                <form-item-table-cell
                  v-else
                  :enterpriseFormVersionItem="item"
                  :reportFormVersionItem="getReportFormVersionItem(item)"
                  :reportFormVersion="reportFormVersion"
                  :updatable="updatable"
                  :isEditing="isEditing"
                  :reportFormVersionId="reportFormVersionId"
                  :rowReportTargetDate="getRowReportTargetDate(row)"
                />
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import isString from "lodash/isString";
import enterpriseFormService from "@/services/enterprises/enterpriseFormService";
import operationReportService from "@/services/operations/operationReportService";
import {
  applyUnit,
  parseCalFormula,
  evalCalFormula,
  calcNextFormItemValue,
  formatReportValueForDisplay,
} from "@/utils/operations";
import FormItemTableCell from "./FormItemTableCell";
import { createNamespacedHelpers, mapGetters as mapRootGetters } from "vuex";
const { mapGetters, mapActions } = createNamespacedHelpers("operationReport");

export default {
  name: "FormItemTable",
  components: {
    FormItemTableCell,
  },
  props: {
    group: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    reportFormVersionItemMapByFormVersionItemId: {
      type: Object,
      default: () => ({}),
    },
    reportFormVersion: {
      type: Object,
      default: () => ({}),
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    updatable: {
      type: Boolean,
      default: false,
    },
    // 書き込み先 ao_enterprise_vessel_report_form_version.id
    reportFormVersionId: {
      type: String,
      required: true,
    },
  },

  computed: {
    ...mapRootGetters({ masterMaps: "operationMasters/masterMaps" }),
    ...mapGetters({
      globalReportFormVersionItemMap:
        "reportFormVersionItemMapByFormVersionItemId",
    }),

    /** CAL式を持つアイテムとそのパース済みフォーミュラの一覧 */
    calItemFormulas() {
      return this.items
        .filter(item => {
          if (!item || !item.default_value) return false;
          return parseCalFormula(item.default_value) !== null;
        })
        .map(item => ({
          item,
          formula: parseCalFormula(item.default_value),
        }));
    },

    // default_value_id を持つアイテムの一覧（購入日・港名などの自動参照用）
    defaultValueIdItems() {
      return this.items.filter(
        item =>
          item && item.default_value_id != null && item.default_value_id !== ""
      );
    },

    hasGroupLabel() {
      return isString(this.group.label) && this.group.label.length > 0;
    },
    columnHeaders() {
      return this.group.column_headers || [];
    },
    hasColumnHeaders() {
      return this.columnHeaders.length > 0;
    },
    rowHeaders() {
      return this.group.row_headers || [];
    },
    hasRowHeaders() {
      return this.rowHeaders.length > 0;
    },
    tableMatrix() {
      return enterpriseFormService.buildTableMatrix(this.items);
    },
  },

  methods: {
    ...mapActions({ setFormVersionItemValue: "setFormVersionItemValue" }),

    formatLabel(label) {
      // JSON などのシリアライズ時に \n がエスケープされている場合に、実際の改行文字に変換する。

      if (!isString(label)) return label;
      return label.replace(/\\n/g, "\n");
    },

    /** default_value_id を持つアイテムを参照元の値で自動同期する（購入日・港名など） */
    autoSyncDefaultValueIdCells() {
      if (!this.isEditing || !this.updatable) return;
      if (this.defaultValueIdItems.length === 0) return;

      for (const item of this.defaultValueIdItems) {
        const srcItem = this.globalReportFormVersionItemMap[
          String(item.default_value_id)
        ];
        if (!srcItem) continue;

        // 参照元から値を取り出す（value_date / value_string / value_number のいずれか）
        const srcValue =
          srcItem.value_date != null
            ? srcItem.value_date
            : srcItem.value_string != null
            ? srcItem.value_string
            : srcItem.value_number != null
            ? srcItem.value_number
            : null;
        if (srcValue === null) continue;

        const reportFormVersionItem = this.getReportFormVersionItem(item);
        // 既に同じ値がセットされている場合はスキップ
        const currentValue =
          reportFormVersionItem.value_date != null
            ? reportFormVersionItem.value_date
            : reportFormVersionItem.value_string != null
            ? reportFormVersionItem.value_string
            : reportFormVersionItem.value_number != null
            ? reportFormVersionItem.value_number
            : null;
        if (currentValue === srcValue) continue;

        const nextFormItemValue = calcNextFormItemValue(
          item,
          reportFormVersionItem,
          { value: srcValue, nullableChecked: false }
        );
        this.setFormVersionItemValue({
          reportFormVersionId: this.reportFormVersionId,
          ...nextFormItemValue,
        });
      }
    },

    getRowHeaderLabel(rowIndex) {
      const header = this.rowHeaders[rowIndex];
      return header ? this.formatLabel(header.label) : "";
    },
    getRowHeaderLines(rowIndex) {
      const label = this.getRowHeaderLabel(rowIndex);
      return label ? label.split("\n") : [""];
    },
    isRowRequired(rowIndex) {
      const header = this.rowHeaders[rowIndex];
      return header ? header.required : false;
    },
    getReportFormVersionItem(item) {
      return this.reportFormVersionItemMapByFormVersionItemId[item.id] || {};
    },
    // 項目単位 editable=false のセルは編集中でも read-only 表示にする
    isItemEditable(item) {
      return enterpriseFormService.isItemEditable(item);
    },
    getCellDisplayValue(item) {
      const reportFormVersionItem = this.getReportFormVersionItem(item);
      const value = operationReportService.getValueFromReportFormVersionItem(
        reportFormVersionItem
      );
      const hasValue = operationReportService.hasReportFormVersionItemValue(
        reportFormVersionItem
      );
      if (!hasValue) return "-";

      // SELECT / RADIO の場合は value をラベルに変換して表示
      const isSelect = enterpriseFormService.isSelect(item);
      const isRadioButton = enterpriseFormService.isRadioButton(item);
      let displayValue;
      if (
        (isSelect || isRadioButton) &&
        value != null &&
        Array.isArray(item.select_options) &&
        item.select_options.length > 0
      ) {
        const selectOptionMapByValue = item.select_options.reduce(
          (acc, option) => {
            if (option && option.value != null) {
              acc[option.value] = option;
            }
            return acc;
          },
          {}
        );
        const selectOption = selectOptionMapByValue[value];
        displayValue =
          selectOption && (selectOption.label || selectOption.text)
            ? selectOption.label || selectOption.text
            : value;
      } else {
        displayValue = formatReportValueForDisplay(
          item,
          value,
          this.masterMaps
        );
      }

      return applyUnit(displayValue, item);
    },
    // 同一行の中で、すでに入力済みの日付（DATE_PICKER / DATETIME_PICKER）の月を「その行の基準月」として返す。
    // 見つからない場合は null（＝v-calendar のデフォルトで今日の月）を返す。
    getRowReportTargetDate(row) {
      for (const item of row) {
        if (!item) continue;
        const isDateLike =
          enterpriseFormService.isDatePickers(item) ||
          enterpriseFormService.isDateTimePickers(item);
        if (!isDateLike) continue;

        const reportFormVersionItem = this.getReportFormVersionItem(item);
        const hasValue = operationReportService.hasReportFormVersionItemValue(
          reportFormVersionItem
        );
        if (!hasValue) continue;

        const value = operationReportService.getValueFromReportFormVersionItem(
          reportFormVersionItem
        );
        if (value) return value;
      }
      return null;
    },
  },

  watch: {
    // 同一テーブル内だけでなく、他フォームの値変更（CAL式が参照するクロスフォーム参照）にも対応するため、
    globalReportFormVersionItemMap() {
      this.$nextTick(() => {
        if (this.calItemFormulas.length > 0) {
          // CAL式を持つ全アイテムを評価し、依存値に応じて自動入力・リセットする

          if (!this.isEditing || !this.updatable) return;
          if (this.calItemFormulas.length === 0) return;

          for (const { item, formula } of this.calItemFormulas) {
            const computedValue = evalCalFormula(
              formula,
              this.globalReportFormVersionItemMap
            );
            const reportFormVersionItem = this.getReportFormVersionItem(item);

            if (computedValue === null) {
              // 全依存値がnullになった場合、計算フィールドもnullにリセット
              if (reportFormVersionItem.value_number != null) {
                const nextFormItemValue = calcNextFormItemValue(
                  item,
                  reportFormVersionItem,
                  { value: null, nullableChecked: false }
                );
                this.setFormVersionItemValue({
                  reportFormVersionId: this.reportFormVersionId,
                  ...nextFormItemValue,
                });
              }
              continue;
            }

            if (reportFormVersionItem.value_number === computedValue) continue;

            const nextFormItemValue = calcNextFormItemValue(
              item,
              reportFormVersionItem,
              { value: computedValue, nullableChecked: false }
            );
            this.setFormVersionItemValue({
              reportFormVersionId: this.reportFormVersionId,
              ...nextFormItemValue,
            });
          }
        }
        if (this.defaultValueIdItems.length > 0)
          this.autoSyncDefaultValueIdCells();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

.form-item-table {
  grid-column: 1 / -1;
  margin: 8px 0;

  .form-item-table-title {
    margin-bottom: 8px;
  }

  .form-item-table-wrapper {
    overflow-x: auto;
  }

  .form-item-table-content {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    border: 1px solid $aisea-light-gray;

    th,
    td {
      border: 1px solid $aisea-light-gray;
      padding: 8px 12px;
      text-align: left;
      vertical-align: middle;
      word-break: break-word;
      overflow-wrap: break-word;
    }

    thead th {
      background-color: #f5f7fa;
      white-space: pre-line;
      text-align: center;

      &.required:after {
        content: " *";
        font-size: 12px;
        color: $aisea-red;
        font-weight: 700;
      }
    }

    .form-item-table-corner {
      background-color: #f5f7fa;
      min-width: 100px;
    }

    .form-item-table-row-header {
      background-color: #f5f7fa;
      min-width: 100px;

      .row-header-line {
        display: block;
      }

      &.required:after {
        content: " *";
        font-size: 12px;
        color: $aisea-red;
        font-weight: 700;
      }
    }

    .form-item-table-cell {
      min-width: 120px;
      white-space: normal;
      word-break: break-word;
      overflow-wrap: break-word;

      .cell-display {
        min-height: 24px;
        white-space: normal;
        word-break: break-word;
        overflow-wrap: break-word;
      }
    }
  }
}
</style>
